import {
	COMPANY_CONTACT_LOOKUP_CREDITS,
	CompanyContactLookup,
	companyContactLookupOf,
	ContactLookupSettings,
	EMAIL_LOOKUP_CREDITS,
	PHONE_LOOKUP_CREDITS,
	personContactLookupsOf,
	PREVIEW_COMPANY_CREDITS,
} from "@signalist/shared-types";

import { CreditSegmentView, formatCredits } from "@/components/CreditConfirmModal";

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * What a preview of `companyCount` companies can be charged for (PREC-4263 review M2). A preview
 * runs the list's own search and the contact lookups its settings ask for
 * (`personContactLookupsOf`, `companyContactLookupOf`, the predicates the API runs), so it is
 * charged per company, person and result found, which no number before the run can promise:
 * every part is variable and named with its shared price. PREVIEW_COMPANY_CREDITS per company is
 * what the API checks the balance against before it starts (`validateCredits`), returned apart.
 */
export function previewQuote(
	companyCount: number,
	list: ContactLookupSettings,
	t: Translate
): { segments: CreditSegmentView[]; requiredToStart: number } {
	const lookups = personContactLookupsOf(list);
	const part = (id: string, label: string, unitCredits: number): CreditSegmentView => ({
		id,
		label,
		count: companyCount,
		unitCredits,
		included: true,
		variable: true,
	});
	const segments = [
		part(
			"SEARCH",
			t("creditConfirm.preview.SEARCH", { count: companyCount }),
			PREVIEW_COMPANY_CREDITS
		),
	];

	if (lookups.email) {
		const unit = formatCredits(EMAIL_LOOKUP_CREDITS);

		segments.push(part("EMAIL", t("creditConfirm.preview.EMAIL", { unit }), EMAIL_LOOKUP_CREDITS));
	}

	if (lookups.phone) {
		const unit = formatCredits(PHONE_LOOKUP_CREDITS);

		segments.push(part("PHONE", t("creditConfirm.preview.PHONE", { unit }), PHONE_LOOKUP_CREDITS));
	}

	if (companyContactLookupOf(list) !== CompanyContactLookup.OFF) {
		const unit = formatCredits(COMPANY_CONTACT_LOOKUP_CREDITS);

		segments.push(
			part(
				"COMPANY_CONTACT",
				t("creditConfirm.preview.COMPANY_CONTACT", { unit }),
				COMPANY_CONTACT_LOOKUP_CREDITS
			)
		);
	}

	return { segments, requiredToStart: companyCount * PREVIEW_COMPANY_CREDITS };
}
